/**
 * Optiewaarden op een Print.com-product die bewust door een ANDERE gekoppelde
 * leverancier worden gemaakt (bijvoorbeeld zwart Forex 3 mm via Probo op het
 * Print.com-product `forex-black`).
 *
 * Eén marker, op de optiewaarde zelf:
 *
 *   value.metadata.supplierRoute = { routed: true, linkId?: '<ProductSupplier.id>' }
 *
 * Bewust zonder leveranciersnaam: de opties gaan ongefilterd naar de browser
 * (productpagina) en leveranciersnamen horen daar niet. Welke leverancier de
 * waarde maakt, volgt uit de dekking (optionCoverage) van de gekoppelde
 * ProductSupplier-rij; `linkId` is alleen een verwijzing voor beheer.
 *
 * Zo'n waarde bestaat per definitie niet in Print.com's catalogus. Daardoor:
 * - mag de catalogusreconciliatie hem niet als "verouderde Print.com-waarde"
 *   verwijderen (catalog-reconciliation.ts);
 * - mag de configurator hem niet bij Print.com laten controleren of als
 *   onbeschikbaar markeren; prijs komt uit /api/pricing/calculate, dat via
 *   resolveSupplierOnlyPricing de gekoppelde leverancier gebruikt;
 * - mag het optiefilter hem niet verbergen omdat er geen Print.com-prijs is.
 *
 * De prijs- en orderroute zelf hoeft niets van deze marker te weten: een
 * waarde die niet in printComConnectedProduct staat is voor
 * printComDefinitelyCannotCover al bewijs dat Print.com hem niet kan maken.
 * Zonder dekkende leverancierskoppeling komt er dus nooit een prijs (fail closed).
 */

type Json = Record<string, any>;

/** Wordt door de opties-API meegestuurd zodat scripts kunnen nagaan of de guards gedeployed zijn. */
export const FOREIGN_SUPPLIER_VALUES_GUARD_VERSION = 1;

const PRINTCOM_SUPPLIER_KEYS = new Set(['printcom', 'print-com', 'print.com']);

/** ProductSupplier-id (of 'linked-supplier') als de waarde naar een andere leverancier gaat, anders null. */
export function foreignSupplierOf(value: unknown): string | null {
  if (!value || typeof value !== 'object') return null;
  const route = (value as Json).metadata?.supplierRoute;
  if (!route || typeof route !== 'object' || Array.isArray(route) || route.routed !== true) return null;
  // Defensive: an explicit Print.com route is not foreign.
  if (typeof route.supplier === 'string' && PRINTCOM_SUPPLIER_KEYS.has(route.supplier.trim().toLowerCase())) return null;
  return typeof route.linkId === 'string' && route.linkId.trim() ? route.linkId.trim() : 'linked-supplier';
}

export function isForeignSupplierValue(value: unknown): boolean {
  return foreignSupplierOf(value) !== null;
}

function optionKey(option: Json): string {
  return String(option?.id ?? option?.name ?? '');
}

function valueKey(value: Json): string {
  return String(value?.value ?? value?.id ?? '');
}

/** optionId -> set van waarden (als string) die naar een andere leverancier gaan. */
export function foreignSupplierValueMap(options: unknown): Map<string, Set<string>> {
  const map = new Map<string, Set<string>>();
  if (!Array.isArray(options)) return map;
  for (const option of options) {
    if (!option || typeof option !== 'object') continue;
    for (const value of Array.isArray(option.values) ? option.values : []) {
      if (!isForeignSupplierValue(value)) continue;
      const key = optionKey(option);
      if (!map.has(key)) map.set(key, new Set());
      map.get(key)!.add(valueKey(value));
      const providerValue = value?.providerValue;
      if (providerValue !== undefined && providerValue !== null) map.get(key)!.add(String(providerValue));
    }
  }
  return map;
}

export function isForeignSupplierSelection(options: unknown, optionId: string, value: unknown): boolean {
  if (value === undefined || value === null || value === '') return false;
  return foreignSupplierValueMap(options).get(String(optionId))?.has(String(value)) === true;
}

/** True als een configuratie minstens één naar een andere leverancier gerouteerde waarde bevat. */
export function configurationHasForeignSupplierValue(options: unknown, configuration: Record<string, unknown> | null | undefined): boolean {
  if (!configuration) return false;
  const map = foreignSupplierValueMap(options);
  for (const [optionId, values] of map) {
    const selected = configuration[optionId];
    if (selected !== undefined && selected !== null && values.has(String(selected))) return true;
  }
  return false;
}
